import type { CSSProperties } from "react";
import { GradientText } from "lola-framework-ui-test";
import { useStore } from "../store/globalState";
import { TransactionItem } from "./TransactionItem";
import type { ITransaction } from "../interfaces/transactions";
import { useTranslation } from "react-i18next";
import { ScrollFadeIndicator } from "./ScrollFadeIndicator";


/** Height (px) the indicator holds while a refresh is in flight. */
const REFRESH_INDICATOR_HEIGHT = 48;

export interface TransactionsHistoryProps {
  items: ITransaction[];
  /** Scroll container (used as the pull-to-refresh target). */
  containerRef?: React.Ref<HTMLDivElement>;
  /** Current pull-to-refresh drag distance (px). */
  pullDistance?: number;
  /** True while the refresh request is running. */
  refreshing?: boolean;
  /** True while the user is actively dragging. */
  pulling?: boolean;
  /** Distance required to trigger a refresh. */
  pullThreshold?: number;
}

export const TransactionsHistory: React.FC<TransactionsHistoryProps> = ({
  items = [],
  containerRef,
  pullDistance = 0,
  refreshing = false,
  pulling = false,
  pullThreshold = 70,
}) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const colorConfig = theme.whiteView;
  const { bodyCopy } = colorConfig;
  const isEmpty = items.length === 0;

  const indicatorHeight = refreshing ? REFRESH_INDICATOR_HEIGHT : pullDistance;
  const reachedThreshold = pullDistance >= pullThreshold;

  return (
    <>
      <GradientText className="normal-text transactions-history-title">
        {t("transactions.title")}
      </GradientText>
      <div ref={containerRef} className="transactions-history-container">
        <ScrollFadeIndicator color={bodyCopy} />
        <div
          className="pull-refresh"
          style={{
            height: indicatorHeight,
            transition: pulling ? "none" : "height 0.25s ease",
          }}
        >
          {refreshing ? (
            <span
              className="loader-spinner pull-refresh-spinner"
              style={
                {
                  "--spinner-start": bodyCopy,
                  "--spinner-end": bodyCopy,
                } as CSSProperties
              }
            />
          ) : (
            <span
              className="pull-refresh-arrow"
              style={{
                color: bodyCopy,
                opacity: Math.min(pullDistance / pullThreshold, 1),
                transform: `rotate(${reachedThreshold ? 180 : 0}deg)`,
              }}
            >
              &darr;
            </span>
          )}
        </div>

        {isEmpty && !refreshing ? (
          <section className="transactions-history-empty">
            <GradientText textColor={bodyCopy} className="empty-title">
              <b>{t("transactions.emptyTitle")}</b>
            </GradientText>
            <GradientText textColor={bodyCopy} className="empty-description">
              {t("transactions.emptyDescription")}
            </GradientText>
          </section>
        ) : (
          <section className="transactions-history-list">
            {items.map((transaction) => (
              <TransactionItem
                key={transaction.originationId ?? transaction.operationId}
                {...transaction}
              />
            ))}
          </section>
        )}
      </div>
    </>
  );
};
